import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService, IUtilsDictionary, LoggingService, UtilsDate, UtilsJson, UtilsMisc, UtilsString } from '@pit/core';
import { EntityDataService } from '@pit/core-data';
import { DtoShipmentProcess, IDtoShipmentProcess, ProductionSystemTransferState } from '@pit/frontend-model';
import { map, Observable } from 'rxjs';
import { IDtoShipmentProcessEntityDataService } from '../interfaces/dto.shipment.process.entity.data.service.interface';

@Injectable({
  providedIn: 'root'
})
export class DtoShipmentProcessEntityDataService extends EntityDataService<IDtoShipmentProcess> implements IDtoShipmentProcessEntityDataService {

  constructor(http: HttpClient, configService: ConfigService, loggingService: LoggingService) {
    super(http, null, configService, loggingService);
  }

  public getActiveShipmentProcessesForDepot$(depotNumber: string): Observable<IDtoShipmentProcess[]> {
    return this.httpGet(`${this.apiUrl}/active/depot/${depotNumber}`, null, 30000)
      .pipe(map((entities: IDtoShipmentProcess[]) => {
        const result: IDtoShipmentProcess[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getActiveShipmentProcessesForDepotAsync(depotNumber: string): Promise<IDtoShipmentProcess[]> {
    return (await this.getActiveShipmentProcessesForDepot$(depotNumber).toPromise());
  }

  public getShipmentProcessesToReturnForDepot$(depotNumber: string, includeInDelivery: boolean = false): Observable<IDtoShipmentProcess[]> {
    return this.httpGet(`${this.apiUrl}/returns/depot/${depotNumber}/${includeInDelivery ? 'true' : 'false'}`, null, 30000)
      .pipe(map((entities: IDtoShipmentProcess[]) => {
        const result: IDtoShipmentProcess[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentProcessesToReturnForDepotAsync(depotNumber: string, includeInDelivery: boolean = false): Promise<IDtoShipmentProcess[]> {
    return (await this.getShipmentProcessesToReturnForDepot$(depotNumber, includeInDelivery).toPromise());
  }

  public setShipmentProcess$(shipmentProcess: IDtoShipmentProcess): Observable<IDtoShipmentProcess> {
    return this.httpPost(`${this.apiUrl}`, shipmentProcess, null, 10000)
      .pipe(map((entity: IDtoShipmentProcess) => {
        if (UtilsMisc.isNullOrUndefinedOrEmpty(entity) === false) {
          return this.createEntityFromJson(entity);
        } else {
          return null;
        }
      }));
  }

  public async setShipmentProcessAsync(shipmentProcess: IDtoShipmentProcess): Promise<IDtoShipmentProcess> {
    return (await this.setShipmentProcess$(shipmentProcess).toPromise());
  }

  public setShipmentProcesses$(shipmentProcesses: IDtoShipmentProcess[]): Observable<IDtoShipmentProcess[]> {
    return this.httpPost(`${this.apiUrl}/multi`, shipmentProcesses, null, 10000)
      .pipe(map((entities: IDtoShipmentProcess[]) => {
        const result: IDtoShipmentProcess[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async setShipmentProcessesAsync(shipmentProcesses: IDtoShipmentProcess[]): Promise<IDtoShipmentProcess[]> {
    return (await this.setShipmentProcesses$(shipmentProcesses).toPromise());
  }

  public getShipmentProcessesForMandatorByProductionSystemTransferState$(mandator: string, transferState: ProductionSystemTransferState): Observable<IDtoShipmentProcess[]> {
    return this.httpGet(`${this.apiUrl}/mandator/${mandator}/transferState/${transferState}`, null, 30000)
      .pipe(map((entities: IDtoShipmentProcess[]) => {
        const result: IDtoShipmentProcess[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentProcessesForMandatorByProductionSystemTransferStateAsync(mandator: string, transferState: ProductionSystemTransferState): Promise<IDtoShipmentProcess[]> {
    return (await this.getShipmentProcessesForMandatorByProductionSystemTransferState$(mandator, transferState).toPromise());
  }

  public getShipmentProcessesByProductionSystemTransferStateForDepot$(transferState: ProductionSystemTransferState, depotNumber: string): Observable<IDtoShipmentProcess[]> {
    return this.httpGet(`${this.apiUrl}/mandator/mandatorCode/transferState/${transferState}/depot/${depotNumber}`, null, 30000)
      .pipe(map((entities: IDtoShipmentProcess[]) => {
        const result: IDtoShipmentProcess[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentProcessesByProductionSystemTransferStateForDepotAsync(transferState: ProductionSystemTransferState, depotNumber: string): Promise<IDtoShipmentProcess[]> {
    return (await this.getShipmentProcessesByProductionSystemTransferStateForDepot$(transferState, depotNumber).toPromise());
  }

  protected override doGetApiUrl(): string {
    return `/api/web/shipmentprocesses`;
  }

  protected override createEntityFromJson(json: any): IDtoShipmentProcess {
    const result: IDtoShipmentProcess = (UtilsJson.deserialize(json, DtoShipmentProcess) as IDtoShipmentProcess);
    if (result !== null) {
      result.setReferences();
    }

    return result;
  }

  protected override doGetUrls(): IUtilsDictionary<string> {
    this._urls = super.doGetUrls();

    this._urls.add('get.active.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/active/depot/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.returns.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/returns/depot/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}/{1}');
    this._urls.add('get.by.transferstate', UtilsString.stringFormat('{0}://{1}:{2}/{3}/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + 'mandator/{0}/transferstate/{1}');
    this._urls.add('get.by.transferstate.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/transferstate/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}/depot/{1}');
    this._urls.add('save.multi', UtilsString.stringFormat('{0}://{1}:{2}/{3}/multi', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl));

    return this._urls;
  }

}
